import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { EvoluraLogo } from "../components/EvoluraLogo";
import { HeartHandshake, ImagePlus, X, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { compressImageToDataUrl } from "../utils/imageCompress";
import SEO from "../components/SEO";

const API = "/api";

const GratitudeRespondPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [content, setContent] = useState("");
  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const run = async () => {
      try {
        const res = await fetch(`${API}/public/respond/${encodeURIComponent(token)}`);
        if (!res.ok) {
          setNotFound(true);
          return;
        }
        const data = await res.json();
        setInfo(data);
        if (data.draft) {
          setContent(data.draft.content || "");
          setImageDataUrl(data.draft.image_data_url || null);
        }
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [token]);

  const saveDraft = useCallback(async (c, img) => {
    if (!c?.trim() && !img) return;
    try {
      await fetch(`${API}/public/respond/${encodeURIComponent(token)}/save-draft`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: c || "", image_data_url: img || null }),
      });
    } catch {
      /* silent, retried on next keystroke */
    }
  }, [token]);

  // Debounced autosave
  useEffect(() => {
    if (loading) return;
    if (!content && !imageDataUrl) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveDraft(content, imageDataUrl), 1200);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
  }, [content, imageDataUrl, loading, saveDraft]);

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image is too large. Please pick one under 10 MB.");
      return;
    }
    setImageUploading(true);
    try {
      const dataUrl = await compressImageToDataUrl(file, {
        maxDim: 1200,
        maxBytes: 900_000,
        mimeType: file.type === "image/png" ? "image/png" : "image/jpeg",
      });
      setImageDataUrl(dataUrl);
    } catch {
      toast.error("Couldn't read that image");
    } finally {
      setImageUploading(false);
    }
  };

  const handleSaveAndSignUp = async () => {
    if (!content.trim() && !imageDataUrl) {
      toast.error("Add a note or an image first");
      return;
    }
    setSaving(true);
    try {
      // Final save
      await saveDraft(content, imageDataUrl);
      // Kick off the invite-accept flow — it goes through Google OAuth first,
      // then promotes the draft to a real card server-side.
      window.location.href = `/invite/${token}`;
    } catch {
      toast.error("Couldn't save — please try again");
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-50 via-white to-indigo-50">
        <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-violet-50 via-white to-indigo-50 px-6 text-center">
        <SEO title="Link not found" description="This invite link isn't valid anymore" noIndex />
        <div className="w-16 h-16 rounded-full bg-violet-100 flex items-center justify-center mb-4">
          <HeartHandshake className="w-8 h-8 text-violet-400" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">This link isn&apos;t valid anymore</h1>
        <p className="text-slate-600 mb-6">The invite may have been revoked or the link is broken.</p>
        <Button onClick={() => navigate("/")} className="bg-violet-600 hover:bg-violet-700 text-white rounded-full">
          Go to Evoluras
        </Button>
      </div>
    );
  }

  if (info?.already_accepted) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-violet-50 via-white to-indigo-50 px-6 text-center">
        <SEO title="Already joined" description="You've already accepted this invite" noIndex />
        <div className="w-16 h-16 rounded-full bg-violet-100 flex items-center justify-center mb-4">
          <CheckCircle2 className="w-8 h-8 text-violet-600" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">You&apos;re already in</h1>
        <p className="text-slate-600 mb-6">Head to your dashboard to respond from your own wall.</p>
        <Button onClick={() => navigate("/dashboard")} className="bg-violet-600 hover:bg-violet-700 text-white rounded-full">
          Open Evoluras
        </Button>
      </div>
    );
  }

  const asker = info?.asker;
  const first = asker?.first_name || "Someone";
  const recipientFirst = (info?.recipient_name || "").split(" ")[0] || "";

  return (
    <>
      <SEO
        title={`${first} is asking: what are you grateful for in me?`}
        description={`Write your note to ${first} and save it on your own Evoluras wall.`}
        path={`/respond/${token}`}
      />
      <div
        className="min-h-screen"
        style={{
          background:
            "radial-gradient(ellipse 1000px 500px at 30% 0%, rgba(238,237,254,0.6) 0%, transparent 60%)," +
            " linear-gradient(180deg, #F8F7FC 0%, #FFFFFF 100%)",
        }}
        data-testid="respond-page"
      >
        <header className="max-w-3xl mx-auto px-6 pt-6">
          <a href="https://www.evoluras.com" aria-label="Evoluras — home" className="inline-flex items-center">
            <EvoluraLogo size={34} />
          </a>
        </header>

        <main className="max-w-2xl mx-auto px-6 py-10 sm:py-14">
          {/* Asker card */}
          <div className="text-center mb-8">
            {asker?.picture && (
              <img
                src={asker.picture}
                alt={asker.name}
                className="w-16 h-16 rounded-full object-cover mx-auto mb-3"
                style={{ border: "3px solid rgba(83,74,183,0.2)" }}
              />
            )}
            <div className="text-xs font-semibold uppercase tracking-widest text-violet-600 mb-2">
              A note is waiting to be written
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ letterSpacing: "-0.01em" }}>
              {first} is asking{recipientFirst ? `, ${recipientFirst},` : ""}
              <br />
              <span className="italic text-violet-700">what are you grateful for in {first}?</span>
            </h1>
            {info?.prompt && (
              <div
                className="mt-5 mx-auto max-w-lg rounded-xl px-5 py-4 text-left"
                style={{
                  background: "var(--color-surface)",
                  border: "1px solid var(--color-border-default)",
                }}
              >
                <div className="text-[10px] font-semibold uppercase tracking-widest text-violet-600 mb-1.5">
                  Their prompt
                </div>
                <p className="text-sm text-slate-800 italic leading-relaxed m-0">
                  &ldquo;{info.prompt}&rdquo;
                </p>
              </div>
            )}
          </div>

          {/* Compose */}
          <div
            className="rounded-2xl p-5 sm:p-6"
            style={{
              background: "var(--color-surface)",
              border: "1px solid var(--color-border-default)",
              boxShadow: "0 10px 40px -20px rgba(83,74,183,0.15)",
            }}
          >
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={5}
              maxLength={600}
              placeholder="Write from the heart — one honest sentence often means more than a paragraph."
              className="resize-none mb-3"
              data-testid="respond-content"
              autoFocus
            />

            {imageDataUrl ? (
              <div
                className="relative rounded-lg overflow-hidden mb-3"
                style={{ border: "1px solid var(--color-border-default)" }}
              >
                <img
                  src={imageDataUrl}
                  alt="Attached"
                  style={{ width: "100%", maxHeight: 220, objectFit: "cover", display: "block" }}
                />
                <button
                  onClick={() => setImageDataUrl(null)}
                  className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center hover:bg-black/80 transition-colors"
                  style={{ background: "rgba(0,0,0,0.6)", color: "#fff" }}
                  aria-label="Remove image"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={imageUploading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm transition-colors mb-3"
                style={{
                  background: "var(--color-surface-subtle)",
                  color: "var(--color-text-secondary)",
                  border: "1px dashed var(--color-border-default)",
                }}
                data-testid="respond-image-pick"
              >
                <ImagePlus size={14} />
                {imageUploading ? "Processing…" : "Add a photo (optional)"}
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleImagePick}
            />

            <Button
              onClick={handleSaveAndSignUp}
              disabled={saving || imageUploading || (!content.trim() && !imageDataUrl)}
              className="w-full rounded-full h-11"
              style={{
                background: "var(--color-brand-primary)",
                color: "var(--color-text-on-brand)",
                boxShadow: "0 4px 14px -4px rgba(83, 74, 183, 0.4)",
              }}
              data-testid="respond-submit"
            >
              {saving ? "Saving…" : (
                <>
                  Save &amp; send to {first}
                  <ArrowRight size={15} className="ml-2" />
                </>
              )}
            </Button>

            <p className="text-xs text-slate-500 text-center mt-3 leading-relaxed">
              You&apos;ll create a free account to save this &mdash; and {first} will see it right away.
              <br />
              Your own wall starts here too. Private by default.
            </p>
          </div>
        </main>
      </div>
    </>
  );
};

export default GratitudeRespondPage;
